import { revisionOutstanding } from './supplierRevision';
import { isArchivedStatus, isOpenStatus } from './types';
import type { CgOrder, CgStatus } from './types';

const DAY_MS = 86_400_000;

/**
 * Whole days from a timestamp to a calendar day.
 *
 * types.daysBetween is date-to-date and builds `${iso}T00:00:00Z`; the values
 * here are timestamptz (status_changed_at, sample_clock_started_at) and that
 * concatenation would produce an invalid date for them. Measuring from the
 * instant to midnight of `today` rounds toward zero, so a stage entered
 * earlier today reads as 0 days rather than 1 — under-reporting, which is the
 * direction this whole feature errs in deliberately.
 */
function daysSince(fromTimestamp: string, todayIso: string): number {
  const from = new Date(fromTimestamp).getTime();
  const to = new Date(`${todayIso}T00:00:00Z`).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return 0;
  }
  return Math.floor((to - from) / DAY_MS);
}

/**
 * The pipeline board's arithmetic, with no React in it.
 *
 * The board answers one question the Orders table cannot: not "what status is
 * this order" — the table already says that — but "what has stopped moving".
 * Everything here exists to turn a status column into that answer.
 */

/** A card's worth of an order, already reduced to what the tile renders. */
export interface PipelineCard {
  order: CgOrder;
  daysInStage: number;
  flags: PipelineFlag[];
}

export interface PipelineFlag {
  label: string;
  /** Severity drives colour. 'wait' is informational, 'late' is a problem. */
  tone: 'wait' | 'late' | 'ready';
}

export interface PipelineColumn {
  status: CgStatus;
  cards: PipelineCard[];
  /** Every card's value added up — the money parked at this stage. */
  value: number;
}

/**
 * How long this order has held its current status.
 *
 * status_changed_at is stamped by the database (20261013000100) and is exact
 * from that migration forward. Older rows carry a backfill from updated_at,
 * which under-reports rather than over-reports — see the migration's note.
 */
export function daysInStage(order: CgOrder, todayIso: string): number {
  return Math.max(0, daysSince(order.status_changed_at, todayIso));
}

/**
 * Whether the sample clock this order started has run out.
 *
 * Only meaningful once a supplier has actually started it; an order with no
 * clock is not late, it simply has no clock.
 */
export function sampleOverdue(order: CgOrder, todayIso: string): boolean {
  if (order.sample_clock_started_at === null) {
    return false;
  }
  return daysSince(order.sample_clock_started_at, todayIso) > order.sample_due_days;
}

/**
 * What is worth saying about this order at a glance.
 *
 * Derived from the order row alone — no per-order lookup. A board that fires
 * one query per card is a board that stops loading at fifty orders, and every
 * fact below is already on the row the list query returns.
 *
 * Deliberately NOT here: whether the financial gate is satisfied. That lives
 * in task rows the board does not read, and a gate flag that silently means
 * "no tasks visible to you" would be worse than no flag — the order drawer
 * states the gate exactly, and it is the place the decision is made.
 */
export function flagsFor(
  order: CgOrder,
  todayIso: string,
  stallDays: number,
  isOpen: boolean,
): PipelineFlag[] {
  const flags: PipelineFlag[] = [];

  // First, because it is the only flag that means the order cannot move at
  // all. A factory has said the PO is not buildable as written and is waiting
  // on an answer; until somebody gives one, nothing else on this card is
  // happening. It is deliberately NOT gated on isOpen — a closed order with an
  // unanswered question is a question that was never answered, and hiding it
  // is how it got missed in the first place.
  if (revisionOutstanding(order)) {
    flags.push({ label: 'Supplier blocked', tone: 'late' });
  }
  // The most actionable state on the board: the factory says it is done and
  // is waiting on somebody at TAP to move the order on.
  if (order.supplier_ready_for_shipment) {
    flags.push({ label: 'Supplier ready', tone: 'ready' });
  }
  if (isOpen && sampleOverdue(order, todayIso)) {
    flags.push({ label: 'Sample overdue', tone: 'late' });
  }
  // Suppressed while a revision request is outstanding, because it would be
  // the opposite of the truth. Both conditions are satisfied at once — the PO
  // is out and unconfirmed, which is exactly the state a factory is in when it
  // has asked a question — and "Awaiting supplier" on an order where the
  // supplier is awaiting US is the misreading that let these sit for weeks.
  if (
    isOpen &&
    order.supplier_confirmed_at === null &&
    order.date_po_sent !== null &&
    !revisionOutstanding(order)
  ) {
    flags.push({ label: 'Awaiting supplier', tone: 'wait' });
  }
  // Stalling is only interesting while the order is still live. A closed order
  // has legitimately sat still since the day it closed, and flagging that
  // would fill the Closed column with alarms nobody can act on.
  const stalled = daysInStage(order, todayIso);
  if (isOpen && stalled >= stallDays) {
    flags.push({ label: `${String(stalled)}d in stage`, tone: 'late' });
  }
  return flags;
}

/**
 * Group orders into their status columns.
 *
 * Columns come from cg_statuses in sort_order so the board matches the
 * dropdown and the reports; an order whose status is not in that table would
 * otherwise vanish from the board entirely, so it is collected into a
 * trailing column rather than dropped — a row you cannot see is a row nobody
 * fixes.
 */
export function pipelineColumns(
  orders: CgOrder[],
  statuses: CgStatus[],
  totals: Map<string, number>,
  todayIso: string,
  stallDays: number,
  /**
   * Hide ARCHIVED statuses — Closed alone. It used to hide everything that
   * was not is_open, which took Shipped and Delivered with it and left the
   * board's last three columns permanently empty.
   */
  hideArchived: boolean,
): PipelineColumn[] {
  const ordered = [...statuses].sort((a, b) => a.sort_order - b.sort_order);
  const known = new Set(ordered.map((status) => status.id));
  const byStatus = new Map<string, PipelineCard[]>();

  for (const order of orders) {
    const isOpen = isOpenStatus(order.status, statuses);
    if (hideArchived && isArchivedStatus(order.status, statuses)) {
      continue;
    }
    const card: PipelineCard = {
      order,
      daysInStage: daysInStage(order, todayIso),
      flags: flagsFor(order, todayIso, stallDays, isOpen),
    };
    const key = known.has(order.status) ? order.status : UNKNOWN_STATUS;
    const list = byStatus.get(key);
    if (list === undefined) {
      byStatus.set(key, [card]);
    } else {
      list.push(card);
    }
  }

  const columns = ordered.map((status) => buildColumn(status, byStatus, totals));

  // Anything whose status is not in cg_statuses — a value typed straight into
  // the API, or a status row deleted while orders still pointed at it. It gets
  // a column rather than being filtered out: an order missing from the board
  // is an order nobody notices is broken.
  const orphans = byStatus.get(UNKNOWN_STATUS);
  if (orphans !== undefined && orphans.length > 0) {
    columns.push(
      buildColumn(
        {
          id: UNKNOWN_STATUS,
          sort_order: Number.MAX_SAFE_INTEGER,
          is_open: true,
          is_archived: false,
          // A status nobody recognises belongs to no pipeline in particular.
          // 'custom' rather than null because the column is not nullable, and
          // this row is a placeholder for the board, never written anywhere.
          order_type: 'custom',
          label: null,
        },
        byStatus,
        totals,
      ),
    );
  }
  return columns;
}

/** The id the board files an order under when cg_statuses does not know it. */
export const UNKNOWN_STATUS = '__unknown__';

function buildColumn(
  status: CgStatus,
  byStatus: Map<string, PipelineCard[]>,
  totals: Map<string, number>,
): PipelineColumn {
  // Longest-stalled first: the top of every column is the thing to look at.
  const cards = (byStatus.get(status.id) ?? []).sort((a, b) => b.daysInStage - a.daysInStage);
  const value = cards.reduce((sum, card) => sum + (totals.get(card.order.id) ?? 0), 0);
  return { status, cards, value };
}

/** Initials for the owner chip. Two letters at most; empty when unknown. */
export function initialsOf(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (parts.length === 0) {
    return '';
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length === 1) {
    return first.slice(0, 2).toUpperCase();
  }
  return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase();
}
